import CurrencyInput from 'react-currency-input-field';
import { Controller, useFormContext } from 'react-hook-form';

import { V1TradeExpirationType } from '@fintronners/react-api/src';

import { WebBodyText14, WebCaption12 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { GoodForDialog } from '@/components/features/trade/GoodForDialog/GoodForDialog';
import { TradeBreakdown } from '@/components/features/trade/TradeBreakdown/TradeBreakdown';
import type { TradeBreakdown as TradeBreakdownType } from '@/hooks/trade/useTradeBreakdown';
import { Radio } from '@/components/common/Radio/Radio';
import { TradeSide, TradeFormData } from '../schema';
import { TradeCurrency, TradeCurrencyError } from './TradeCurrency';

const displayStrings = {
  [V1TradeExpirationType.Day]: '1 Day',
  [V1TradeExpirationType.GoodTilCancel]: 'Good Till Cancel',
};

interface TradeLimitBuyFormProps {
  tradeBreakdown: TradeBreakdownType;
  isFractionable?: boolean;
}

export const TradeLimitBuyForm = ({ tradeBreakdown, isFractionable }: TradeLimitBuyFormProps) => {
  const { control } = useFormContext<TradeFormData>();

  return (
    <div className="flex flex-col mb-8">
      <div>
        <TradeCurrency side={TradeSide.BUY} showUnitToggler isFractionable={isFractionable} />

        <div className="mb-2">
          <TradeCurrencyError />
        </div>

        <div>
          <WebBodyText14.Regular className="text-grey72">Enter Limit Price</WebBodyText14.Regular>
          <WebCaption12.Regular className="text-grey72">
            Buy when the share price falls below:
          </WebCaption12.Regular>
          <Controller
            control={control}
            name="limitPrice"
            render={({ field: { value, onChange } }) => (
              <CurrencyInput
                className="mt-2 w-full border rounded h-[35px] px-3 py-2 text-sm text-darkestBlue focus-visible:outline-primary"
                prefix="$"
                value={value}
                placeholder="$0.00"
                decimalsLimit={2}
                allowNegativeValue={false}
                intlConfig={{ locale: 'en-US' }}
                onValueChange={(_value) => onChange(_value)}
              />
            )}
          />
        </div>

        <div className="mt-5">
          <TradeBreakdown tradeBreakdown={tradeBreakdown} />
        </div>
      </div>
      <div className="mt-8">
        <GoodForDialog />
        <ul className="flex gap-4 mt-2">
          {[V1TradeExpirationType.Day, V1TradeExpirationType.GoodTilCancel].map(
            (expirationType) => (
              <li key={expirationType} className="w-full">
                <Controller
                  name="duration"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Radio.Input
                        id={expirationType}
                        value={expirationType}
                        onChange={() => onChange?.(expirationType as V1TradeExpirationType)}
                        checked={expirationType === value}
                      />
                      <Radio.Label variant="secondary" htmlFor={expirationType.toString()}>
                        {displayStrings[expirationType]}
                      </Radio.Label>
                    </>
                  )}
                />
              </li>
            ),
          )}
        </ul>
      </div>
    </div>
  );
};
